/**
 * Event Handlers Module
 * Manages all document-level event listeners and event bindings
 */

import { NotificationManager } from '../utils/notifications.js';

export class EventHandlers {
  constructor(app) {
    this.app = app;
  }

  setupAllEventListeners() {
    this.bindComponentEvents();
    this.setupCartIntegration();
  }

  bindComponentEvents() {
    this.app.welcomeScreen.bindEvents(
      () => this.app.navigateToMenu(),
      null,
      () => this.app.navigateToMenu()
    );

    this.app.menuView.bindEvents();

    this.app.restaurantSelector.bindEvents(
      (restaurantId) => this.app.switchRestaurant(restaurantId),
      () => this.app.reloadConfiguration(),
      () => this.app.testAPIConnection()
    );
  }

  setupCartIntegration() {
    if (this.app.cartIntegrationSetup) {
      return;
    }
    this.app.cartIntegrationSetup = true;

    this.setupItemAddedEvent();
    this.setupOrderConfirmationEvent();
    this.setupOrderEditEvent();
    this.setupConfirmOrderEvent();
    this.setupBeforeOrderMethodEvent();
    this.setupLanguageEvents();
    this.setupNavigationEvents();
    this.setupCartManagerListener();
    this.setupPaymentEvents();
    this.setupCartManipulationEvents();
    this.setupOrderManagementEvents();
  }

  setupItemAddedEvent() {
    document.addEventListener('itemAddedToCart', (event) => {
      const item = event.detail;

      const now = Date.now();
      const lastAddTime = this.app.lastItemAddTime || 0;
      const timeDiff = now - lastAddTime;

      if (timeDiff < 500) {
        console.log('[MainApp] Duplicate item addition prevented:', item.name);
        return;
      }

      this.app.lastItemAddTime = now;

      const newCartCount = this.app.cartManager.addItem(item);
      this.app.menuView.updateCartCount(newCartCount);
      this.animateCartButton();

      const cartItems = this.app.cartManager.getItems();
      const orderState = this.app.cartManager.getOrderState();
      const pendingOrder = this.app.cartManager.getPendingOrder();

      console.log('[MainApp] Showing cart notepad:', {
        cartItems: cartItems.length,
        item: item?.name,
        orderState,
        hasPendingOrder: !!pendingOrder
      });

      this.app.cartNotepad.show(cartItems, item, orderState, pendingOrder);
    });
  }

  setupOrderConfirmationEvent() {
    document.addEventListener('showOrderConfirmation', (e) => {
      const cartItems = this.app.cartManager.getItems();
      const totals = this.app.cartManager.getTotals();
      const config = this.app.configManager.getCurrentConfig();
      const timing = config?.paymentFlowConfiguration?.paymentTiming;
      const isBeforeOrder = (timing ? timing === 'BEFORE_ORDER' : true);

      if (cartItems.length === 0) {
        this.showOrderHistoryNotepad();
        return;
      }

      if (isBeforeOrder) {
        const currency = cartItems[0]?.currency || config?.currency || 'PLN';
        const pseudoOrder = { id: null, items: cartItems, total: totals.total, currency };
        this.app.unifiedPaymentModal.show(pseudoOrder, () => {});
        return;
      }

      this.app.orderConfirmationModal?.show(cartItems, totals);
    });
  }

  setupOrderEditEvent() {
    document.addEventListener('editOrder', (event) => {
      NotificationManager.info('You can continue editing your order');
    });
  }

  setupConfirmOrderEvent() {
    document.addEventListener('confirmOrder', (event) => {
      const orderData = event.detail;
      const config = this.app.configManager.getCurrentConfig();
      const timing = config?.paymentFlowConfiguration?.paymentTiming;
      const isBeforeOrder = (timing ? timing === 'BEFORE_ORDER' : true);

      if (isBeforeOrder) {
        const cartItems = this.app.cartManager.getItems();
        const totals = this.app.cartManager.getTotals();
        const currency = cartItems[0]?.currency || this.app.configManager.getCurrentConfig()?.currency || 'PLN';
        const pseudoOrder = { id: null, items: cartItems, total: totals.total, currency };
        this.app.unifiedPaymentModal.show(pseudoOrder, () => {});
        if (orderData.paymentMethod) {
          this.app.unifiedPaymentModal.selectPaymentMethod(orderData.paymentMethod);
        }
        return;
      }

      this.app.handleOrderConfirmation(orderData);
    });
  }

  setupBeforeOrderMethodEvent() {
    document.addEventListener('beforeOrderMethodSelected', async (event) => {
      const { paymentMethod, orderItems, orderTotals } = event.detail || {};
      const config = this.app.configManager.getCurrentConfig();
      const isBeforeOrder = config?.paymentFlowConfiguration?.paymentTiming === 'BEFORE_ORDER';
      if (!isBeforeOrder) return;

      try {
        const menuData = this.app.configManager.getMenuData();
        const paymentProvider = paymentMethod === 'qr_code' ? 'TPAY' : undefined;
        const orderPayload = this.app.orderService.createOrderPayload(orderItems, orderTotals, menuData, { type: 'dine-in', paymentProvider });
        const order = await this.app.orderService.createOrder(orderPayload);
        this.app.currentOrder = order;
        this.app.cartManager.setPendingOrder(order);

        if (paymentMethod !== 'cash' && paymentMethod !== 'desk_payment' && paymentMethod !== 'staff_payment') {
          this.app.showPaymentNotification('Payment Required', 'required');
          this.app.unifiedPaymentModal.show(order, () => {
            this.app.hidePaymentNotification();
          });
          this.app.unifiedPaymentModal.selectPaymentMethod(paymentMethod);
        } else {
          this.app.showPaymentNotification('Waiting for staff confirmation', 'pending');
        }
      } catch (e) {
        console.error('[MainApp] BEFORE_ORDER draft creation failed:', e);
        NotificationManager.error('Could not create order. Please try again.');
      }
    });
  }

  setupLanguageEvents() {
    document.addEventListener('showLanguageSelector', async () => {
      await this.app.languageSelector.show();
    });

    document.addEventListener('languageChanged', (event) => {
      const newLanguage = event.detail.language;
      if (this.app.i18n && this.app.i18n.getCurrentLanguage && this.app.i18n.getCurrentLanguage() !== newLanguage) {
        this.app.i18n.setLanguage(newLanguage);
      }
      if (this.app.languageSelector && typeof this.app.languageSelector.setCurrentLanguage === 'function') {
        this.app.languageSelector.setCurrentLanguage(newLanguage);
      }
      if (this.app.cartNotepad) {
        this.app.cartNotepad.i18n = this.app.i18n;
      }
      this.app.refreshCurrentView();
    });
  }

  setupNavigationEvents() {
    document.addEventListener('showStaffCallModal', () => {
      this.app.staffCallModal.show();
    });

    document.addEventListener('navigateToWelcome', () => {
      this.app.navigateToWelcome();
    });
  }

  setupCartManagerListener() {
    this.app.cartManager.addListener((items, count, totals) => {
      this.app.menuView.updateCartCount(count);
    });
  }

  setupPaymentEvents() {
    document.addEventListener('paymentCompleted', (event) => {
      const { payment, action, order } = event.detail;
      if (action === 'continue') {
        if (order) {
          this.app.currentOrder = order;
          this.app.cartManager.setPendingOrder(order);
        }
        this.app.handlePaymentSuccess(payment);
      }
    });

    document.addEventListener('paymentRetry', (event) => {
      const { payment } = event.detail;
      this.app.handlePaymentRetry(payment);
    });

    document.addEventListener('paymentCancelled', (event) => {
      const { payment } = event.detail;
      this.app.handlePaymentCancellation(payment);
    });

    document.addEventListener('paymentResultAction', (event) => {
      const { action, result } = event.detail;
      this.app.handlePaymentResultAction(action, result);
    });

    document.addEventListener('payAllUnpaidOrders', (event) => {
      const { orderIds } = event.detail || {};
      if (!orderIds || orderIds.length === 0) return;
      console.log('[MainApp] Initiating group payment for orders:', orderIds);
      this.app.paymentService.initiateGroupPayment(orderIds, 'qr_code').then((payment) => {
        this.app.showPaymentNotification('Payment Required', 'required');
        const total = (this.app.lastUnpaidOrders || []).filter(o => orderIds.includes(o.id)).reduce((sum, o) => sum + (o.total || 0), 0);
        const currency = (this.app.lastUnpaidOrders || [])[0]?.currency || 'PLN';
        this.app.unifiedPaymentModal.show({ id: orderIds[0], items: [], total, currency }, () => {
          this.app.hidePaymentNotification();
        });
      }).catch((err) => {
        console.error('[MainApp] Group payment failed:', err);
        this.app.showPaymentNotification('Payment init failed', 'error');
        setTimeout(() => this.app.hidePaymentNotification(), 3000);
      })
    });

    document.addEventListener('payOrders', (event) => {
      const { orderIds } = event.detail || {};
      if (!orderIds || orderIds.length === 0) return;
      this.app.paymentService.initiateGroupPayment(orderIds, 'qr_code').then(() => {
        this.app.showPaymentNotification('Payment Required', 'required');
        const total = (this.app.lastUnpaidOrders || []).filter(o => orderIds.includes(o.id)).reduce((sum, o) => sum + (o.total || 0), 0);
        const currency = (this.app.lastUnpaidOrders || [])[0]?.currency || 'PLN';
        this.app.unifiedPaymentModal.show({ id: orderIds[0], items: [], total, currency }, () => {
          this.app.hidePaymentNotification();
        });
      }).catch((err) => {
        console.error('[MainApp] Group/single payment failed:', err);
        this.app.showPaymentNotification('Payment init failed', 'error');
        setTimeout(() => this.app.hidePaymentNotification(), 3000);
      })
    });

    document.addEventListener('paymentSessionTimeout', (event) => {
      const { order } = event.detail;
      this.app.handlePaymentSessionTimeout(order);
    });

    document.addEventListener('staffNotification', (event) => {
      const { type, color, message, orderId } = event.detail;
      this.app.handleStaffNotification(type, color, message, orderId);
    });

    document.addEventListener('continuePayment', (event) => {
      const { order } = event.detail;
      if (order) {
        this.app.showPaymentNotification('Online Payment Required', 'required');
        this.app.unifiedPaymentModal.show(order, () => {
          this.app.hidePaymentNotification();
        });
      }
    });
  }

  setupCartManipulationEvents() {
    document.addEventListener('cartUpdateQuantity', (e) => {
      const { cartId, quantity } = e.detail || {}
      if (cartId == null || quantity == null) return
      this.app.cartManager.updateItemQuantity(cartId, quantity)
      const items = this.app.cartManager.getItems()
      const totals = this.app.cartManager.getTotals()
      const currency = (items[0]?.currency) || (this.app.configManager.getCurrentConfig()?.currency) || 'PLN'
      this.app.unifiedPaymentModal.updateFromCart(items, totals, currency)
      this.app.menuView.updateCartCount(this.app.cartManager.getItemCount())
    })

    document.addEventListener('cartRemoveItem', (e) => {
      const { cartId } = e.detail || {}
      if (cartId == null) return
      this.app.cartManager.removeItem(cartId)
      const items = this.app.cartManager.getItems()
      const totals = this.app.cartManager.getTotals()
      const currency = (items[0]?.currency) || (this.app.configManager.getCurrentConfig()?.currency) || 'PLN'
      this.app.unifiedPaymentModal.updateFromCart(items, totals, currency)
      this.app.menuView.updateCartCount(this.app.cartManager.getItemCount())
    })
  }

  setupOrderManagementEvents() {
    document.addEventListener('confirmCartOrder', async (event) => {
      const items = this.app.cartManager.getItems();
      if (!items || items.length === 0) return;
      const totals = this.app.cartManager.getTotals();
      const menuData = this.app.configManager.getMenuData();
      const orderPayload = this.app.orderService.createOrderPayload(items, totals, menuData, { type: 'dine-in' });
      try {
        const order = await this.app.orderService.createOrder(orderPayload);
        this.app.currentOrder = order;
        this.app.cartManager.setPendingOrder(order);
        this.app.cartManager.clear?.();
        this.app.menuView.updateCartCount(this.app.cartManager.getItemCount());
        this.app.showPaymentNotification('Payment Required', 'required');
      } catch (e) {
        console.error('[MainApp] Failed to confirm cart order:', e);
        NotificationManager.error('Failed to send order to kitchen');
      }
    });

    document.addEventListener('confirmExistingOrder', (event) => {
      const { orderId } = event.detail || {};
      if (!orderId) return;
      NotificationManager.info('Order confirmed. You can pay at any time.');
    });

    document.addEventListener('cancelPendingOrder', (event) => {
      this.app.cartManager.cancelPendingOrder();
      this.app.menuView.updateCartCount(this.app.cartManager.getItemCount());
      NotificationManager.info('Order cancelled. Items restored to cart.');
    });
  }

  animateCartButton() {
    const cartButton = document.querySelector('.cart-btn, #sidebar-cart-btn');
    if (cartButton) {
      cartButton.style.transform = 'scale(1.2)';
      cartButton.style.background = '#2d5a3d';

      setTimeout(() => {
        cartButton.style.transform = 'scale(1)';
        cartButton.style.background = '';
      }, 200);
    }
  }

  showOrderHistoryNotepad() {
    const emptyHistoryContent = {
      title: 'Order History',
      subtitle: 'Your recent orders will appear here',
      action: 'Start ordering to see your history',
      items: []
    };

    this.app.cartNotepad.showOrderHistory?.(emptyHistoryContent) ||
    this.app.cartNotepad.show([], null, emptyHistoryContent);
  }
}
